// @ts-strict-ignore
import type { IconDefinition } from "@fullscript/fontawesome-common-types";
import { useEffect, useRef } from "react";

import { Button } from "@aviary/components/Button";
import type { ButtonProps } from "@aviary/components/Button/Button.types";

import { useDropdownContext } from "../../DropdownContext";
import { DropdownChevron } from "../DropdownChevron";

import { useDropdownButtonProps } from "./useDropdownButtonProps";

import * as styles from "./DropdownButton.styles";

interface Props
  extends Omit<ButtonProps, "isColor" | "intention" | "isText" | "isOutlined" | "isLight"> {
  /**
   * if true, button will have chevron icon, or custom icon
   *
   * @default true
   */
  hasIcon?: boolean;
  /**
   * optional custom icon that will be used in place of chevron
   */
  customIcon?: IconDefinition;
  /**
   * Centers the text content
   *
   * @default false
   */
  isCentered?: boolean;
  /**
   * Option to have primary intention dropdown button
   */
  intention?: Extract<ButtonProps["intention"], "primary" | "secondary">;
}

/**
 * @deprecated Use `DropdownMenu.Trigger` with Button from `@fullscript/aviary-web` instead.
 */
const DropdownButton = ({ hasIcon = true, isCentered = false, ...props }: Props) => {
  const dropdownProps = useDropdownButtonProps();
  const { isDropdownOpen, prefixText, triggerElement, setTriggerElement } = useDropdownContext();

  const triggerRef = useRef(null);

  const initializeElement = () => {
    if (!triggerElement) {
      setTriggerElement(triggerRef.current);
    }
  };

  useEffect(() => {
    initializeElement();
  }, []);

  const { children, customIcon, intention = "secondary", ...rest } = props;

  const renderIcon = () => {
    if (hasIcon) {
      return <DropdownChevron customIcon={customIcon} />;
    }
  };

  const renderPrefix = () => {
    return (
      prefixText && (
        <span css={styles.prefix} data-testid="prefix-text">
          {prefixText}:
        </span>
      )
    );
  };

  const buttonStyles = [
    styles.dropdownButton,
    isCentered && styles.isCentered.button,
    intention === "secondary" && styles.color,
  ];

  const contentWrapperStyles = [
    styles.dropdownButtonContentWrapper,
    isCentered && styles.isCentered.contentWrapper,
  ];

  return (
    <Button
      {...dropdownProps}
      innerRef={triggerRef}
      css={buttonStyles}
      intention={intention}
      {...rest}
      aria-haspopup="true"
      aria-expanded={isDropdownOpen}
    >
      <div css={contentWrapperStyles}>
        {renderPrefix()}
        <span css={styles.text}>{children}</span>
      </div>
      {renderIcon()}
    </Button>
  );
};

export { DropdownButton, type Props };
